'use strict'

const CLASS_INVALID = 'is-invalid'
const CLASS_IS_CHECKED = 'is-checked'
const CLASS_ERROR_MESSAGE = 'error'
const CLASS_HAS_ERROR = 'has-error'
const CLASS_FORM = 'js-validate'
const CLASS_SERVICES = 'js-services'
const CLASS_PASSWORD = 'js-password'
const CLASS_PASSWORD_CONFIRM = 'js-password-confirm'

const SELECTOR_FORM_ELEMENTS = 'input, select, textarea'
const SELECTOR_INPUT = 'input[type="text"], input[type="email"], input[type="password"]'
const SELECTOR_CLICK = 'input[type="checkbox"], input[type="radio"]'
const SELECTOR_ERROR_MESSAGE = `.${CLASS_ERROR_MESSAGE}`
const SELECTOR_INVALID = `.${CLASS_INVALID}`

import errorMessages from '../config/errorMessages'
import { checkPasswordValidity, checkEmailValidity } from '../utils/utils'

export default {
  /**
   * @description Initialise validation
   * @returns {undefined}
   */
  init() {
    this.bind()
    this.currentForm = null
  },
  
  /**
   * @description Bind events on form
   * @returns {undefined}
   */
  bind() {
    document.getElementsByClassName(CLASS_FORM)
            .bindEvent('submit', this.validateForm.bind(this))
  },
  
  /**
   * @description Loop through all form's input, calling toggleField on each of them and binding
   * events on SELECTOR_INPUT_REQUIRED and SELECTOR_CLICK_REQUIRED if form is invalid
   * @param {object} e MouseEvent
   * @returns {undefined} none
   */
  validateForm(e) {
    this.currentForm = e.currentTarget
    this.currentForm.querySelectorAll(SELECTOR_FORM_ELEMENTS).forEach(item => {
      this.toggleField(item, this.validateField(item))
    })
    this.clearIsChecked(this.currentForm)
    
    let focusItem = this.currentForm.querySelector(SELECTOR_INVALID)
    if (focusItem) {
      e.preventDefault()
      if (focusItem.tagName === 'FIELDSET') {
        focusItem = focusItem.querySelector(SELECTOR_FORM_ELEMENTS)
      }
      focusItem.focus()
      
      if (!this.currentForm.classList.contains(CLASS_HAS_ERROR)) {
        this.currentForm.classList.add(CLASS_HAS_ERROR)
        this.currentForm.querySelectorAll(SELECTOR_INPUT).bindEvent('input', (e) => {
          this.toggleField(e.currentTarget, this.validateField(e.currentTarget))
          this.clearIsChecked(this.currentForm)
        })
        this.currentForm.querySelectorAll(SELECTOR_CLICK).bindEvent('click', (e) => {
          this.toggleField(e.currentTarget, this.validateField(e.currentTarget))
          this.clearIsChecked(this.currentForm)
        })
      }
    }
  },
  
  /**
   * @description check the tagName of form's field (and type if input) and test if empty or if
   * pattern invalid
   * @param {element} item form's input
   * @returns {object} contains boolean isValid (true if the field is valid) and boolean/object
   * isPatternInvalid (false if pattern is correct or if field is empty OR an object containing
   * the error message related to the type of input)
   */
  validateField(item) {
    const value = item.value
    let isValid = false
    let isPatternInvalid = false
  
    switch (item.tagName) {
      case 'SELECT':
        if (value || !item.required) {
          isValid = true
        }
        break
      case 'TEXTAREA':
        ({ isValid, isPatternInvalid } = this.checkFieldValidity(item))
        break
      case 'INPUT':
        switch (item.getAttribute('type')) {
          case 'text':
            ({ isValid, isPatternInvalid } = this.checkFieldValidity(item))
            break
          case 'email':
            if (checkEmailValidity(value)) {
              isValid = true
            } else if (value) {
              isPatternInvalid = { message: errorMessages.email }
            }
            break
          case 'password':
            if (checkPasswordValidity(value)) {
              isValid = true
              if (item.classList.contains(CLASS_PASSWORD_CONFIRM) && !this.isSamePassword(item)) {
                isValid = false
                isPatternInvalid = { message: errorMessages.passwordConfirm }
              }
            } else if (value) {
              isPatternInvalid = { message: errorMessages.password }
            }
            break
          case 'checkbox':
            if (item.classList.contains(CLASS_SERVICES)) {
              const fieldset = item.closest('fieldset')
              if (fieldset.classList.contains(CLASS_IS_CHECKED)) {
                break
              }
              Array.prototype.forEach.call(fieldset.getElementsByClassName(CLASS_SERVICES), service => {
                if (service.checked || !service.required) {
                  isValid = true
                }
              })
            } else if (item.checked || !item.required) {
              isValid = true
            }
            break
          case 'radio':
            if (this.currentForm.querySelectorAll(`[name="${item.name}"]:checked`).length
                || !item.required) {
              isValid = true
            }
            break
          default:
        }
        break
      default:
    }
    return { isValid: isValid, isPatternInvalid: isPatternInvalid }
  },
  
  /**
   * @description call toggleError and toggle CLASS_INVALID depending of the input validity
   * @param {element} item form's input
   * @param {element} isValidObj validateField function return
   * @returns {undefined}
   */
  toggleField(item, isValidObj) {
    if (item.closest('fieldset') && item.closest('fieldset').classList.contains(CLASS_IS_CHECKED)) {
      return
    }
    this.toggleError(item, isValidObj)
    if (item.type === 'radio'
        || (item.type === 'checkbox' && item.classList.contains(CLASS_SERVICES))) {
      item = item.closest('fieldset')
      if (item.type === 'checkbox') {
        item.classList.add(CLASS_IS_CHECKED)
      }
    }
    if (isValidObj.isValid) {
      item.classList.remove(CLASS_INVALID)
    } else {
      item.classList.add(CLASS_INVALID)
    }
  },
  
  /**
   * @description add/remove/update error message depending of the input validity
   * @param {element} item form's input
   * @param {element} isValidObj validateField function return
   * @returns {undefined}
   */
  toggleError(item, isValidObj) {
    let itemParent = item.parentNode
    if (item.type === 'radio'
        || (item.type === 'checkbox' && item.classList.contains(CLASS_SERVICES))) {
      itemParent = item.closest('fieldset')
    }
    const error = itemParent.querySelector(SELECTOR_ERROR_MESSAGE)
    const errorMsg = isValidObj.isPatternInvalid ?
                     isValidObj.isPatternInvalid.message : errorMessages.empty
    
    if (isValidObj.isValid) {
      if (error) {
        error.remove()
      }
    } else if (error) {
      error.innerText = errorMsg
    } else {
      let errorContainer = document.createElement('p')
      errorContainer.classList.add(CLASS_ERROR_MESSAGE)
      errorContainer.innerText = errorMsg
      itemParent.appendChild(errorContainer)
    }
  },
  
  checkFieldValidity(item) {
    const value = item.value
    const pattern = item.pattern
    let isValid = false
    let isPatternInvalid = false
    
    if (value || !item.required) {
      isValid = true
    }
    if (value && pattern && !value.match(new RegExp(pattern))) {
      isValid = false
      isPatternInvalid = { message: item.title }
    }
  
    return { isValid: isValid, isPatternInvalid: isPatternInvalid }
  },
  
  /**
   * @description test if item value is equal to form password input value
   * @param {element} item form's input
   * @returns {boolean} if password and confirm password are the same
   */
  isSamePassword(item) {
    return this.currentForm.getElementsByClassName(CLASS_PASSWORD)[0].value === item.value
  },
  
  clearIsChecked(container) {
    Array.prototype.forEach.call(container.getElementsByClassName(CLASS_IS_CHECKED), element => {
      element.classList.remove(CLASS_IS_CHECKED)
    })
  }
}



// WEBPACK FOOTER //
// ./src/apps/Front/private/js/components/validation.js